import { AccordionStep, FieldSubject, FinancialObligationOptions, LoadingState, MortgageCalculationState } from '@hb/domain';
import { aMortgageCalculation, AssumptionBuilder, money, MortgageCalculationResponseBuilder, MortgageDetailBuilder } from '@hb/builder';
import { AccordionStateService } from '@hb/services/accordion-state/accordion-state.service';
import { ErrorService } from '@hb/services/error/error.service';
import { MortgageService } from '@hb/services/mortgage/mortgage.service';
import { StateService } from '@hb/services/state/state.service';
import { AnalyticsService } from '@hb/services/analytics/analytics.service';

import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime, filter, map, mergeWith, Observable, take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import {
    Applicants,
    ExtraAsset,
    FinancialObligation,
    MortgageCalculationResponse,
    Property,
    PropertyType
} from '@ap/ap-interface-apix-hypotheken-berekening';
import { ApFileDownloadService, DownloadMimeType } from '@ap/file-download';

@Injectable({
    providedIn: 'root'
})
export class StoreService {
    private readonly errorService = inject(ErrorService);
    private readonly mortgageService = inject(MortgageService);
    private readonly stateService = inject(StateService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly accordionStateService = inject(AccordionStateService);
    private readonly destroyRef = inject(DestroyRef);

    public isCalculatingMaxMortgage = false;
    loadingStateDownload = new BehaviorSubject<LoadingState>(LoadingState.IDLE);

    private readonly stateSubject: BehaviorSubject<MortgageCalculationState>;
    private readonly hasCoApplicant: BehaviorSubject<boolean>;

    private readonly applicantsSubject: FieldSubject<Applicants>;
    private readonly financialObligationsOptionsSubject: FieldSubject<FinancialObligationOptions>;
    private readonly financialObligationsSubject: FieldSubject<FinancialObligation>;
    private readonly propertySubject: FieldSubject<Property>;
    private readonly extraAssetSubject: FieldSubject<ExtraAsset>;

    private readonly mortgageSubject = new BehaviorSubject<MortgageCalculationResponse>(
        new MortgageCalculationResponseBuilder()
            .withMortgage(
                aMortgageCalculation
                    .withMaxMortgageByIncome(
                        new MortgageDetailBuilder()
                            .withAmount(money(0))
                            .withGrossMonthlyAmount(money(0))
                            .withNetMonthlyAmount(money(0))
                            .withAssumption(new AssumptionBuilder().withDuration(0).withInterest(0).build())
                            .build()
                    )
                    .build()
            )
            .build()
    );

    constructor() {
        const initialState = this.getInitialState();
        this.stateSubject = new BehaviorSubject(initialState);
        this.applicantsSubject = new FieldSubject<Applicants>(initialState?.applicants);
        this.hasCoApplicant = new BehaviorSubject<boolean>(
            this.applicantsSubject.value?.co_applicant !== undefined && this.applicantsSubject.value?.co_applicant !== null
        );

        this.financialObligationsOptionsSubject = new FieldSubject<FinancialObligationOptions>(initialState?.financialObligationsOptions);
        this.financialObligationsSubject = new FieldSubject<FinancialObligation>(initialState?.financialObligations);
        this.propertySubject = new FieldSubject<Property>(initialState?.property);
        this.extraAssetSubject = new FieldSubject<ExtraAsset>(initialState?.extraAsset);

        this.stateSubject
            .asObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => this.stateService.set(state));

        this.getApplicants()
            .pipe(
                mergeWith(this.getFinancialObligation(), this.getProperty(), this.getExtraAsset()),
                map(() => (this.isCalculatingMaxMortgage = true)),
                debounceTime(800),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.updateStateAndPost());

        this.financialObligationsOptionsSubject
            .asObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateState());

        this.getProperty()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((property) => {
                if (property.property_type === PropertyType.NONE) {
                    this.accordionStateService.setStepNotVisible(AccordionStep.EXTRA_ASSET);
                } else {
                    this.accordionStateService.setStepVisible(AccordionStep.EXTRA_ASSET);
                }
            });

        this.sendAnalyticsResultsOnCalculationDone();
    }

    updateStateAndPost(): void {
        const mortgageCalculation = this.createMortgageCalculation();
        this.mortgageService.postCalculateMaxMortgage(mortgageCalculation).subscribe({
            next: (response: MortgageCalculationResponse) => {
                this.mortgageSubject.next(response);
                this.resolveError();
            },
            error: (error: HttpErrorResponse) => {
                this.publishError(error);
                this.isCalculatingMaxMortgage = false;
            },
            complete: () => (this.isCalculatingMaxMortgage = false)
        });
        this.updateState();
    }

    download(): void {
        this.loadingStateDownload.next(LoadingState.LOADING);
        const mortgageCalculation = this.createMortgageCalculation();
        this.mortgageService.postCalculateMaxMortgageDownload(mortgageCalculation).subscribe({
            next: (resource: Blob) => {
                ApFileDownloadService.initiateBrowserDownload('Hypotheekberekening', resource, DownloadMimeType.PDF);
                this.loadingStateDownload.next(LoadingState.COMPLETED);
            },
            error: () => {
                this.loadingStateDownload.next(LoadingState.FAILED);
            }
        });
    }

    getLoadingStateDownload(): Observable<LoadingState> {
        return this.loadingStateDownload.asObservable();
    }

    private updateState(): void {
        const mortgageCalculation = this.createMortgageCalculation();
        this.stateSubject.next(mortgageCalculation);
    }

    private createMortgageCalculation(): MortgageCalculationState {
        return {
            applicants: this.applicantsSubject.value,
            financialObligationsOptions: this.financialObligationsOptionsSubject.value,
            financialObligations: this.financialObligationsSubject.value,
            property: this.propertySubject.value,
            extraAsset: this.extraAssetSubject.value
        } as MortgageCalculationState;
    }

    private resolveError(): void {
        this.errorService.publish(null);
    }

    private publishError(errorResponse: HttpErrorResponse): void {
        this.errorService.publish(errorResponse);
    }

    private getInitialState(): MortgageCalculationState {
        return this.stateService.get();
    }

    getApplicants(): Observable<Applicants> {
        return this.applicantsSubject.asObservable();
    }

    setApplicants(applicants: Applicants): void {
        this.applicantsSubject.next(applicants);
        this.hasCoApplicant.next(
            applicants != null &&
                applicants.co_applicant !== null &&
                applicants.co_applicant !== undefined &&
                applicants.co_applicant.income !== null &&
                applicants.co_applicant.income !== undefined
        );
    }

    getHasCoApplicant(): Observable<boolean> {
        return this.hasCoApplicant.asObservable();
    }

    getFinancialObligation(): Observable<FinancialObligation> {
        return this.financialObligationsSubject.asObservable();
    }

    setFinancialObligation(financialObligation: FinancialObligation): void {
        this.financialObligationsSubject.next(financialObligation);
    }

    getProperty(): Observable<Property> {
        return this.propertySubject.asObservable();
    }

    setProperty(property: Property): void {
        this.propertySubject.next(property);
    }

    getExtraAsset(): Observable<ExtraAsset> {
        return this.extraAssetSubject.asObservable();
    }

    setExtraAsset(extraAsset: ExtraAsset): void {
        this.extraAssetSubject.next(extraAsset);
    }

    getState(): MortgageCalculationState {
        return this.stateSubject.value;
    }

    getMortgage(): Observable<MortgageCalculationResponse> {
        return this.mortgageSubject.asObservable();
    }

    setFinancialObligationOptions(options: FinancialObligationOptions): void {
        this.financialObligationsOptionsSubject.next(options);
    }

    nextStep(): void {
        this.mortgageSubject
            .asObservable()
            .pipe(take(1), debounceTime(2500))
            .subscribe((result) => {
                this.analyticsService.sendResult(this.accordionStateService.getOpenAccordionStep().step, result);
            });
        this.accordionStateService.nextAccordionStep();
    }

    private sendAnalyticsResultsOnCalculationDone(): void {
        this.accordionStateService
            .getOpenAccordionStepObservable()
            .pipe(
                filter((step: AccordionStep) => step === AccordionStep.MORTGAGE),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((step: AccordionStep) => this.analyticsService.sendResult(step, this.mortgageSubject.value));
    }
}
